import {Signal} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {AbstractControl, FormArray, FormGroup} from '@angular/forms';
import {map, merge, Observable} from 'rxjs';

export type StepStatus = 'empty' | 'filled' | 'invalid';

export type StepPlace = 'upcoming' | 'current' | 'passed';

export type StepPosition = {
    stepId: string;
    travel: number;
};

export type StepState = {
    place: StepPlace;
    status: StepStatus;
    travel: number;
};

export const currentFilledStep: StepState = {
    place: 'current',
    status: 'filled',
    travel: 0,
};

export function settledPosition(stepId: string): StepPosition {
    return {stepId, travel: 0};
}

export function samePosition(first: StepPosition | null, second: StepPosition | null): boolean {
    return first?.stepId === second?.stepId && first?.travel === second?.travel;
}

export function stepStates(
    stepIds: ReadonlyArray<string>,
    currentId: string | null,
    position: StepPosition | null,
    statuses: ReadonlyMap<string, StepStatus>,
): ReadonlyMap<string, StepState> {
    const currentIndex = currentId === null ? -1 : stepIds.indexOf(currentId);
    const moving = position !== null && position.stepId === currentId ? position : null;
    return new Map(
        stepIds.map((stepId, index) => {
            const status = statuses.get(stepId) ?? 'filled';
            const state: StepState =
                index === currentIndex
                    ? {
                          place: 'current',
                          status,
                          travel: moving?.travel ?? 0,
                      }
                    : passedOrUpcoming(index < currentIndex, status);
            return [stepId, state] as const;
        }),
    );
}

function passedOrUpcoming(passed: boolean, status: StepStatus): StepState {
    return passed ? {place: 'passed', status, travel: 1} : {place: 'upcoming', status, travel: 0};
}

export function controlStepStatus(control: AbstractControl): Signal<StepStatus> {
    return toSignal(treeEvents(control).pipe(map(() => statusOf(control))), {
        initialValue: statusOf(control),
    });
}

function statusOf(control: AbstractControl): StepStatus {
    if (!control.invalid) {
        return 'filled';
    }
    return showsError(control) ? 'invalid' : 'empty';
}

function showsError(control: AbstractControl): boolean {
    if (!control.invalid || !control.touched) {
        return false;
    }
    return control.errors != null || childrenOf(control).some(showsError);
}

function treeEvents(control: AbstractControl): Observable<unknown> {
    return merge(control.events, ...childrenOf(control).map(treeEvents));
}

function childrenOf(control: AbstractControl): AbstractControl[] {
    return control instanceof FormGroup || control instanceof FormArray
        ? Object.values<AbstractControl>(control.controls)
        : [];
}
